/** Banner decision in localStorage. Pure: GTM head script and client store share it. */
export const CONSENT_STORAGE_KEY = "hv_consent";

/** Bump when banner services change: everyone gets asked again. */
export const CONSENT_REVISION = 2;

/** Ask again after 12 months. */
export const CONSENT_MAX_AGE_MS: number = 365 * 24 * 60 * 60 * 1000;

/** One switch per banner service. Google consent never implies Meta. */
export const CONSENT_CATEGORIES = ["statistics", "google_ads", "meta"] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

export type ConsentChoices = Readonly<Record<ConsentCategory, boolean>>;

export interface ConsentRecord extends ConsentChoices {
  readonly revision: number;
  readonly decided_at: number;
}

export const NO_CONSENT: ConsentChoices = {
  statistics: false,
  google_ads: false,
  meta: false,
};

export const FULL_CONSENT: ConsentChoices = {
  statistics: true,
  google_ads: true,
  meta: true,
};

/** Null = ask again: missing, malformed, outdated revision or expired. */
export function parseConsentRecord(
  raw: string | null,
  now: number,
): ConsentRecord | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const revision: unknown = Reflect.get(parsed, "revision");
  const decidedAt: unknown = Reflect.get(parsed, "decided_at");
  const statistics: unknown = Reflect.get(parsed, "statistics");
  const googleAds: unknown = Reflect.get(parsed, "google_ads");
  const meta: unknown = Reflect.get(parsed, "meta");
  if (revision !== CONSENT_REVISION) return null;
  if (typeof decidedAt !== "number" || !Number.isFinite(decidedAt)) {
    return null;
  }
  if (now - decidedAt >= CONSENT_MAX_AGE_MS) return null;
  if (
    typeof statistics !== "boolean" ||
    typeof googleAds !== "boolean" ||
    typeof meta !== "boolean"
  ) {
    return null;
  }
  return {
    revision,
    decided_at: decidedAt,
    statistics,
    google_ads: googleAds,
    meta,
  };
}

export function serializeConsentRecord(
  choices: ConsentChoices,
  now: number,
): string {
  const record: ConsentRecord = {
    revision: CONSENT_REVISION,
    decided_at: now,
    statistics: choices.statistics,
    google_ads: choices.google_ads,
    meta: choices.meta,
  };
  return JSON.stringify(record);
}
